<template>
  <div class="max-w-4xl mx-auto">
    <Head title="Crear productos" />
    
    <!-- Header -->
    <div class="mb-8">
      <div class="flex items-center gap-4 text-sm text-gray-500 mb-2">
        <button class="hover:text-orange-500 transition-colors" @click="back">
          <i class="fa fa-arrow-left" />
        </button>
        <Link href="/products" class="hover:text-orange-500 transition-colors">Productos</Link>
        <span class="text-gray-300">/</span>
        <span class="text-gray-900 font-medium">Crear</span>
      </div>
      
      <h1 class="text-2xl font-bold text-gray-900">Registrar Nuevo Producto</h1>
      <p class="text-gray-500 mt-1">Complete los detalles para añadir un nuevo producto al inventario.</p>
    </div>

    <!-- Form -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <form @submit.prevent="store">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-8 p-8">
          <!-- Basic Info -->
          <div class="md:col-span-2">
            <h2 class="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
              <i class="fa fa-info-circle text-orange-500" />
              Información Básica
            </h2>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <!-- Code -->
              <div class="space-y-1.5">
                <div class="flex items-center justify-between">
                  <label class="block text-sm font-medium text-gray-700">Código</label>
                  <span class="text-xs text-gray-400 font-normal">Opcional (se autogenerará si está vacío)</span>
                </div>
                <div class="relative">
                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <i class="fa fa-barcode text-gray-400" />
                  </div>
                  <input 
                    v-model="form.code"
                    type="text" 
                    class="block w-full pl-10 pr-3 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-orange-500 focus:border-orange-500 placeholder-gray-400 transition-colors text-sm"
                    :class="{'border-red-300': form.errors.code}"
                    placeholder="Ej. PROD-0001 (o dejar vacío para autogenerar)"
                  />
                </div>
                <p v-if="form.errors.code" class="text-red-500 text-xs mt-1">{{ form.errors.code }}</p>
              </div>

              <!-- Name -->
              <div class="space-y-1.5">
                <label class="block text-sm font-medium text-gray-700">Nombre *</label>
                <div class="relative">
                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <i class="fa fa-box text-gray-400" />
                  </div>
                  <input 
                    v-model="form.name"
                    type="text" 
                    class="block w-full pl-10 pr-3 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-orange-500 focus:border-orange-500 placeholder-gray-400 transition-colors text-sm"
                    :class="{'border-red-300': form.errors.name}"
                    placeholder="Ingrese el nombre del producto"
                  />
                </div>
                <p v-if="form.errors.name" class="text-red-500 text-xs mt-1">{{ form.errors.name }}</p>
              </div>

              <!-- Type -->
              <div class="space-y-1.5 md:col-span-2">
                <label class="block text-sm font-medium text-gray-700">Tipo de producto *</label>
                <div class="relative">
                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <i class="fa fa-shapes text-gray-400" />
                  </div>
                  <select 
                    v-model="form.type_product"
                    class="block w-full pl-10 pr-10 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-orange-500 focus:border-orange-500 appearance-none transition-colors text-sm"
                    :class="{'border-red-300': form.errors.type_product}"
                  >
                    <option value="">Seleccionar tipo</option>
                    <option value="product">Producto Estándar</option>
                    <option value="service">Servicio</option>
                    <option value="insumo">Insumo / Ingrediente</option>
                    <option value="recipe">Elaborado / Receta</option>
                  </select>
                  <div class="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                    <i class="fa fa-chevron-down text-gray-400 text-xs" />
                  </div>
                </div>
                <p v-if="form.errors.type_product" class="text-red-500 text-xs mt-1">{{ form.errors.type_product }}</p>
              </div>

              <!-- Insumo Helper Notice -->
              <div v-if="form.type_product === 'insumo'" class="md:col-span-2 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
                <div class="w-8 h-8 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i class="fa fa-boxes-stacked" />
                </div>
                <div>
                  <h4 class="text-sm font-semibold text-amber-900">Insumo / Materia Prima</h4>
                  <p class="text-xs text-amber-700 mt-0.5">
                    Este producto no se ofrecerá directamente en canales de venta (POS, Ventas regulares ni Catálogo). Se gestionará su inventario en Compras/Inventarios y se consumirá automáticamente al vender los productos Elaborados/Recetas que lo contengan.
                  </p>
                </div>
              </div>

              <!-- Recipe Builder -->
              <div v-if="form.type_product === 'recipe'" class="md:col-span-2">
                <recipe-builder
                  :items="form.recipe_items"
                  :insumos="insumos"
                  :available-ingredients="availableIngredients"
                  @update:items="form.recipe_items = $event"
                  @cost-changed="onRecipeCostChanged"
                />
                <p v-if="form.errors.recipe_items" class="text-red-500 text-xs mt-1">{{ form.errors.recipe_items }}</p>
              </div>

              <!-- Description -->
              <div class="space-y-1.5 md:col-span-2">
                <label class="block text-sm font-medium text-gray-700">Descripción</label>
                <div class="relative">
                  <div class="absolute inset-y-0 left-0 pl-3 pt-3 pointer-events-none">
                    <i class="fa fa-align-left text-gray-400" />
                  </div>
                  <textarea 
                    v-model="form.description"
                    class="block w-full pl-10 pr-3 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-orange-500 focus:border-orange-500 placeholder-gray-400 transition-colors text-sm"
                    :class="{'border-red-300': form.errors.description}"
                    placeholder="Ingrese una descripción"
                    rows="3"
                  />
                </div>
                <p v-if="form.errors.description" class="text-red-500 text-xs mt-1">{{ form.errors.description }}</p>
              </div>
            </div>
          </div>

          <div class="border-t border-gray-100 col-span-1 md:col-span-2 my-2" />

          <!-- Pricing -->
          <div class="md:col-span-2">
            <h2 class="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
              <i class="fa fa-tag text-orange-500" />
              Precios y Costos
            </h2>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <!-- Cost -->
              <div class="space-y-1.5">
                <label class="block text-sm font-medium text-gray-700">Costo *</label>
                <div class="relative">
                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <i class="fa fa-money-bill text-gray-400" />
                  </div>
                  <input 
                    v-model="form.cost"
                    type="number" 
                    min="0"
                    step="0.01"
                    class="block w-full pl-10 pr-3 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-orange-500 focus:border-orange-500 placeholder-gray-400 transition-colors text-sm"
                    :class="{'border-red-300': form.errors.cost}"
                    placeholder="0.00"
                  />
                </div>
                <p v-if="form.errors.cost" class="text-red-500 text-xs mt-1">{{ form.errors.cost }}</p>
              </div>

              <!-- Dynamic Prices -->
              <div v-for="(group, key) in groups" :key="key" class="space-y-1.5">
                <div class="flex justify-between items-center">
                  <label class="block text-sm font-medium text-gray-700">Precio {{ group.name }} *</label>
                  <span v-if="form.cost > 0 && form.prices[key] > 0 && Number(form.prices[key]) < Number(form.cost)" class="text-xs text-red-500 font-medium flex items-center gap-1">
                    <i class="fa fa-exclamation-triangle" /> Menor al costo
                  </span>
                </div>
                <div class="relative">
                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <i class="fa fa-tag text-gray-400" />
                  </div>
                  <input 
                    v-model="form.prices[key]"
                    type="number" 
                    min="0.01"
                    step="0.01"
                    class="block w-full pl-10 pr-3 py-2.5 bg-white border rounded-lg focus:ring-orange-500 focus:border-orange-500 placeholder-gray-400 transition-colors text-sm"
                    :class="(form.errors['prices.' + key] || form.errors.prices || (form.cost > 0 && form.prices[key] > 0 && Number(form.prices[key]) < Number(form.cost))) ? 'border-red-500 bg-red-50/20' : 'border-gray-200'"
                    placeholder="0.00"
                  />
                </div>
                <p v-if="form.errors['prices.' + key]" class="text-red-500 text-xs mt-1">{{ form.errors['prices.' + key] }}</p>
                <p v-else-if="form.errors.prices" class="text-red-500 text-xs mt-1">{{ form.errors.prices }}</p>
              </div>
            </div>
          </div>

          <div class="border-t border-gray-100 col-span-1 md:col-span-2 my-2" />

          <!-- Categories -->
          <div class="md:col-span-2">
            <h2 class="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
              <i class="fa fa-layer-group text-orange-500" />
              Categorización
            </h2>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <!-- Brand -->
              <div class="space-y-1.5">
                <div class="flex items-center justify-between">
                  <label class="block text-sm font-medium text-gray-700">Marca *</label>
                  <button 
                    type="button" 
                    class="text-xs font-semibold text-orange-600 hover:text-orange-700 flex items-center gap-1 transition-colors" 
                    title="Crear nueva marca rápidamente"
                    @click="openBrandModal"
                  >
                    <i class="fa fa-plus-circle" /> Nueva marca
                  </button>
                </div>
                <div class="relative">
                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <i class="fa fa-bookmark text-gray-400" />
                  </div>
                  <select 
                    v-model="form.brand_id"
                    class="block w-full pl-10 pr-10 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-orange-500 focus:border-orange-500 appearance-none transition-colors text-sm"
                    :class="{'border-red-300': form.errors.brand_id}"
                  >
                    <option value="">Seleccionar marca</option>
                    <option v-for="brand in localBrands" :key="brand.id" :value="brand.id">
                      {{ brand.name }}
                    </option>
                  </select>
                  <div class="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                    <i class="fa fa-chevron-down text-gray-400 text-xs" />
                  </div>
                </div>
                <p v-if="form.errors.brand_id" class="text-red-500 text-xs mt-1">{{ form.errors.brand_id }}</p>
              </div>

              <!-- Category -->
              <div class="space-y-1.5">
                <div class="flex items-center justify-between">
                  <label class="block text-sm font-medium text-gray-700">Categoría</label>
                  <button 
                    type="button" 
                    class="text-xs font-semibold text-orange-600 hover:text-orange-700 flex items-center gap-1 transition-colors" 
                    title="Crear nueva categoría rápidamente"
                    @click="openCategoryModal"
                  >
                    <i class="fa fa-plus-circle" /> Nueva categoría
                  </button>
                </div>
                <div class="relative">
                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <i class="fa fa-tags text-gray-400" />
                  </div>
                  <select 
                    v-model="form.product_category_id"
                    class="block w-full pl-10 pr-10 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-orange-500 focus:border-orange-500 appearance-none transition-colors text-sm"
                    :class="{'border-red-300': form.errors.product_category_id}"
                  >
                    <option value="">Seleccionar categoría</option>
                    <option v-for="category in localProductCategories" :key="category.id" :value="category.id">
                      {{ category.name }}
                    </option>
                  </select>
                  <div class="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                    <i class="fa fa-chevron-down text-gray-400 text-xs" />
                  </div>
                </div>
                <p v-if="form.errors.product_category_id" class="text-red-500 text-xs mt-1">{{ form.errors.product_category_id }}</p>
              </div>

              <!-- Contable -->
              <div class="space-y-1.5">
                <label class="block text-sm font-medium text-gray-700">¿Es contable? *</label>
                <div class="relative">
                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <i class="fa fa-calculator text-gray-400" />
                  </div>
                  <select 
                    v-model="form.contable"
                    class="block w-full pl-10 pr-10 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-orange-500 focus:border-orange-500 appearance-none transition-colors text-sm"
                    :class="{'border-red-300': form.errors.contable}"
                  >
                    <option value="1">Sí</option>
                    <option value="0">No</option>
                  </select>
                  <div class="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                    <i class="fa fa-chevron-down text-gray-400 text-xs" />
                  </div>
                </div>
                <p v-if="form.errors.contable" class="text-red-500 text-xs mt-1">{{ form.errors.contable }}</p>
              </div>

              <file-input
                v-model="form.photo"
                :error="form.errors.photo"
                accept="image/*"
                label="Foto del producto"
              />
            </div>
          </div>
        </div>

        <!-- Submit -->
        <div class="flex items-center justify-end px-8 py-4 bg-gray-50 border-t border-gray-100">
          <loading-button 
            :loading="form.processing" 
            class="px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition-colors flex items-center gap-2 shadow-sm"
            type="submit"
          >
            <i class="fa fa-plus-circle" />
            Crear producto
          </loading-button>
        </div>
      </form>
    </div>

    <!-- Quick Create Brand Modal -->
    <div v-if="showBrandModal" class="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div class="bg-white rounded-2xl shadow-xl border border-gray-100 max-w-md w-full p-6 transition-all" @click.stop>
        <div class="flex items-center justify-between pb-3 border-b border-gray-100">
          <div class="flex items-center gap-2">
            <div class="w-8 h-8 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center">
              <i class="fa fa-bookmark" />
            </div>
            <h3 class="text-base font-bold text-gray-900">Crear Nueva Marca</h3>
          </div>
          <button type="button" class="text-gray-400 hover:text-gray-600" @click="showBrandModal = false">
            <i class="fa fa-times text-lg" />
          </button>
        </div>

        <form class="mt-4 space-y-4" @submit.prevent="quickCreateBrand">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Nombre de la marca *</label>
            <input 
              ref="brandInputRef"
              v-model="newBrandName" 
              type="text" 
              class="block w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500 text-sm"
              placeholder="Ej. Nike, Samsung, etc."
              :disabled="isSubmittingBrand"
            />
            <p v-if="brandError" class="text-xs text-red-500 mt-1">{{ brandError }}</p>
          </div>

          <div class="flex items-center justify-end gap-3 pt-2">
            <button 
              type="button" 
              :disabled="isSubmittingBrand"
              class="px-4 py-2 border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
              @click="showBrandModal = false"
            >
              Cancelar
            </button>
            <button 
              type="submit" 
              :disabled="isSubmittingBrand || !newBrandName.trim()"
              class="px-5 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              <i v-if="isSubmittingBrand" class="fa fa-spinner fa-spin" />
              <span v-else>Guardar Marca</span>
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Quick Create Category Modal -->
    <div v-if="showCategoryModal" class="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div class="bg-white rounded-2xl shadow-xl border border-gray-100 max-w-md w-full p-6 transition-all" @click.stop>
        <div class="flex items-center justify-between pb-3 border-b border-gray-100">
          <div class="flex items-center gap-2">
            <div class="w-8 h-8 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center">
              <i class="fa fa-tags" />
            </div>
            <h3 class="text-base font-bold text-gray-900">Crear Nueva Categoría</h3>
          </div>
          <button type="button" class="text-gray-400 hover:text-gray-600" @click="showCategoryModal = false">
            <i class="fa fa-times text-lg" />
          </button>
        </div>

        <form class="mt-4 space-y-4" @submit.prevent="quickCreateCategory">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Nombre de la categoría *</label>
            <input 
              ref="categoryInputRef"
              v-model="newCategoryName" 
              type="text" 
              class="block w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500 text-sm"
              placeholder="Ej. Bebidas, Alimentos, Repuestos, etc."
              :disabled="isSubmittingCategory"
            />
            <p v-if="categoryError" class="text-xs text-red-500 mt-1">{{ categoryError }}</p>
          </div>

          <div class="flex items-center justify-end gap-3 pt-2">
            <button 
              type="button" 
              :disabled="isSubmittingCategory"
              class="px-4 py-2 border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
              @click="showCategoryModal = false"
            >
              Cancelar
            </button>
            <button 
              type="submit" 
              :disabled="isSubmittingCategory || !newCategoryName.trim()"
              class="px-5 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              <i v-if="isSubmittingCategory" class="fa fa-spinner fa-spin" />
              <span v-else>Guardar Categoría</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import LoadingButton from '@/Shared/LoadingButton'
import FileInput from '@/Shared/FileInput'
import RecipeBuilder from './Partials/RecipeBuilder.vue'
import axios from 'axios'

export default {
  components: {
    Head,
    Link,
    LoadingButton,
    FileInput,
    RecipeBuilder,
  },
  layout: Layout,
  remember: 'form',
  props: {
    groups: {
      type: Object,
      required: true,
    },
    brands: {
      type: [Array, Object],
      required: true,
    },
    productCategories: {
      type: [Array, Object],
      required: true,
    },
    insumos: {
      type: Array,
      default: () => [],
    },
    availableIngredients: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    const brandList = Array.isArray(this.brands) ? [...this.brands] : Object.values(this.brands)
    const categoryList = Array.isArray(this.productCategories) ? [...this.productCategories] : Object.values(this.productCategories)

    return {
      localBrands: brandList,
      localProductCategories: categoryList,
      showBrandModal: false,
      showCategoryModal: false,
      newBrandName: '',
      newCategoryName: '',
      isSubmittingBrand: false,
      isSubmittingCategory: false,
      brandError: '',
      categoryError: '',
      form: this.$inertia.form({
        code: '',
        name: '',
        description: '',
        prices: this.groups.map(() => 0),
        brand_id: brandList.length > 0 ? brandList[0].id : '',
        volumen: '',
        weight: '',
        quantity: 0,
        contable: 1,
        alias: '',
        line_name: '',
        cost: 0,
        product_category_id: categoryList.length > 0 ? categoryList[0].id : '',
        photo: null,
        type_product: 'product',
        recipe_items: [],
      }),
    }
  },
  methods: {
    back() {
      window.history.back()
    },
    openBrandModal() {
      this.newBrandName = ''
      this.brandError = ''
      this.showBrandModal = true
      this.$nextTick(() => {
        this.$refs.brandInputRef?.focus()
      })
    },
    openCategoryModal() {
      this.newCategoryName = ''
      this.categoryError = ''
      this.showCategoryModal = true
      this.$nextTick(() => {
        this.$refs.categoryInputRef?.focus()
      })
    },
    async quickCreateBrand() {
      if (!this.newBrandName || !this.newBrandName.trim()) {
        this.brandError = 'El nombre de la marca es obligatorio.'
        return
      }

      this.isSubmittingBrand = true
      this.brandError = ''

      try {
        const response = await axios.post('/brands', {
          name: this.newBrandName.trim(),
        })

        if (response.data && response.data.brand) {
          const createdBrand = response.data.brand
          this.localBrands.push(createdBrand)
          this.form.brand_id = createdBrand.id
          this.showBrandModal = false
          this.newBrandName = ''
        } else {
          this.brandError = 'No se pudo obtener la marca creada.'
        }
      } catch (error) {
        if (error.response?.data?.errors?.name) {
          this.brandError = error.response.data.errors.name[0]
        } else if (error.response?.data?.message) {
          this.brandError = error.response.data.message
        } else {
          this.brandError = 'Ocurrió un error al crear la marca.'
        }
      } finally {
        this.isSubmittingBrand = false
      }
    },
    async quickCreateCategory() {
      if (!this.newCategoryName || !this.newCategoryName.trim()) {
        this.categoryError = 'El nombre de la categoría es obligatorio.'
        return
      }

      this.isSubmittingCategory = true
      this.categoryError = ''

      try {
        const response = await axios.post('/product-categories', {
          name: this.newCategoryName.trim(),
        })

        if (response.data && response.data.category) {
          const createdCategory = response.data.category
          this.localProductCategories.push(createdCategory)
          this.form.product_category_id = createdCategory.id
          this.showCategoryModal = false
          this.newCategoryName = ''
        } else {
          this.categoryError = 'No se pudo obtener la categoría creada.'
        }
      } catch (error) {
        if (error.response?.data?.errors?.name) {
          this.categoryError = error.response.data.errors.name[0]
        } else if (error.response?.data?.message) {
          this.categoryError = error.response.data.message
        } else {
          this.categoryError = 'Ocurrió un error al crear la categoría.'
        }
      } finally {
        this.isSubmittingCategory = false
      }
    },
    onRecipeCostChanged(cost) {
      if (this.form.type_product === 'recipe') {
        this.form.cost = Number(cost).toFixed(2)
      }
    },
    store() {
      if (this.form.type_product === 'recipe') {
        if (!this.form.recipe_items || !this.form.recipe_items.length) {
          alert('Debes agregar al menos un insumo o ingrediente a la receta.')
          return
        }
      }

      if (this.form.type_product === 'insumo') {
        // Para insumos, si los precios están en 0, igualar al costo para evitar errores
        for (let i = 0; i < this.form.prices.length; i++) {
          if (Number(this.form.prices[i]) <= 0) {
            this.form.prices[i] = Number(this.form.cost) || 0.01
          }
        }
      }


      if (this.form.type_product !== 'insumo') {
        for (let i = 0; i < this.form.prices.length; i++) {
          const price = Number(this.form.prices[i])
          if (price < 0.01) {
            alert(`El precio para el grupo ${this.groups[i]?.name || (i + 1)} debe ser mayor o igual a 0.01.`)
            return
          }
          if (price < Number(this.form.cost)) {
            alert(`El precio para el grupo ${this.groups[i]?.name || (i + 1)} ($${price}) no puede ser menor al costo ($${this.form.cost}).`)
            return
          }
        }
      }

      this.form.post('/products')
    },
  },
}
</script>
